import { Immutable } from "immer";
import React from "react";
import { UGCGrid } from "tsi-common-react/src/components/UGCGrid/index";
import { UGCListSubBlock } from "tsi-common-react/src/models/ugc";
import { fromSSRData } from "tsi-common-react/src/utils/ssr";
import { t } from "ttag";

import type { UGCGrid as UGCGridValue } from "@reactivated";

import { EditorContent } from "@thelabnyc/thelabui/src/components/EditorContent";

/**
 * tempurpedic ``ugc_grid`` (``UGCGrid``) -- the homepage user-generated-content
 * grid (#33588 HomePage ``modules`` consolidation).
 *
 * CONVERTED off the SSR-V1 JSON-string contract: the legacy ``ugc-grid.html``
 * ran ``streamfield_client_render_json value`` and the shared ``UGCGridTemplate``
 * ``JSON.parse``d ``block_data`` into ``{posts}``. ``UGCGrid`` is now registered
 * as a client-renderable block (TP-local; the shared ``UGCGridTemplate`` is
 * untouched -- 6 other sites still re-export it), so its native ``value``
 * (title / desc / posts / cta) is serialized by Reactivated and consumed typed
 * here, with NO ``JSON.parse``.
 *
 * REFACTOR (legacy ``ugc-grid.html`` wrapper): the ``.ugc-grid l-capped-width``
 * wrapper, the optional ``.ugc-grid__header`` (title + raw-HTML desc), the
 * shared ``<UGCGrid>`` tiles component, and the optional ``.ugc-grid__cta`` link.
 * The shared ``UGCGrid`` component renders ONLY the tiles, so the wrapper stays
 * here (it lived in the Django ``.html``, not the React island).
 *
 * POSTS TYPE BRIDGE: the ``UGCPost`` snippet has no Reactivated ``json_schema``,
 * so the generated type for ``value.posts[].post`` is ``null`` even though the
 * runtime serializer (``get_data_for_client_render``) produces the full post
 * data (the same plain-string ``UGCListSubBlockData`` shape the legacy JSON
 * carried). Bridge it through the sanctioned ``fromSSRData`` helper (the
 * SSR-serialization -> branded-type boundary utility), exactly as TempurLove's
 * UGC SSR data does -- NOT a raw ``as`` cast.
 */
type IProps = Immutable<{ value: UGCGridValue }>;

export const UGCGridBlock = (props: IProps): React.JSX.Element => {
    const value = props.value;
    const tiles = fromSSRData<UGCListSubBlock[]>(value.posts ?? []);
    return (
        <div className="ugc-grid l-capped-width">
            {value.title || value.desc ? (
                <div className="ugc-grid__header">
                    {value.title ? (
                        <h2 className="ugc-grid__title">{value.title}</h2>
                    ) : null}
                    {value.desc ? (
                        <EditorContent>
                            {(marker) => (
                                <div
                                    className="ugc-grid__desc"
                                    dangerouslySetInnerHTML={{
                                        __html: value.desc ?? "",
                                    }}
                                    {...marker}
                                />
                            )}
                        </EditorContent>
                    ) : null}
                </div>
            ) : null}
            <UGCGrid tiles={tiles} />
            {value.cta ? (
                <a className="ugc-grid__cta" href={value.cta.url}>
                    {t`View More`}
                </a>
            ) : null}
        </div>
    );
};
